import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { usePublicClient, useWriteContract } from "wagmi";
import { formatEther, parseEther } from "viem";
import NftCard from "../components/common/NftCard";
import Panel from "../components/common/Panel";
import Seo from "../components/common/Seo";
import { LOCALES, t } from "../i18n/messages";
import {
  apiGetMarketNfts,
  apiGetPurchaseHistory,
  apiNotifyShopTx,
} from "../api/backend";
import { useAppState } from "../state/AppStateProvider";
import { buildPath } from "../utils/routing";
import {
  DEFAULT_NFT_ADDRESS,
  NFT_SALE_CONTRACT_ADDRESS,
  USDT_CONTRACT_ADDRESS,
} from "../utils/contractConstants";
import nftSaleAbi from "../abi/NftSaleContract.json";
import {
  resolveNftGalleryImages,
  resolveNftImage,
  resolveTokenId,
} from "../utils/nftMedia";

const MARKET_QUOTE_SYMBOL = "USDT";
const ERC20_ABI = [
  {
    type: "function",
    name: "balanceOf",
    stateMutability: "view",
    inputs: [{ name: "owner", type: "address" }],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    type: "function",
    name: "allowance",
    stateMutability: "view",
    inputs: [
      { name: "owner", type: "address" },
      { name: "spender", type: "address" },
    ],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    type: "function",
    name: "approve",
    stateMutability: "nonpayable",
    inputs: [
      { name: "spender", type: "address" },
      { name: "amount", type: "uint256" },
    ],
    outputs: [{ name: "", type: "bool" }],
  },
];

function normalizePriceString(value) {
  const text = String(value ?? "").trim();
  if (!text || /e/i.test(text)) return "";
  if (!/^\d+(\.\d+)?$/.test(text)) return "";
  return text.replace(/^0+(?=\d)/, "") || "0";
}

function trimDisplayAmount(value, maxDecimals = 6) {
  const text = normalizePriceString(value);
  if (!text) return "";
  const [intPart, fracPart = ""] = text.split(".");
  const trimmedFrac = fracPart.slice(0, maxDecimals).replace(/0+$/, "");
  return trimmedFrac ? `${intPart}.${trimmedFrac}` : intPart;
}

function shortAddress(value) {
  const text = String(value || "").trim();
  if (!text) return "-";
  if (text.length <= 14) return text;
  return `${text.slice(0, 6)}...${text.slice(-4)}`;
}

export default function MarketPage({ locale }) {
  const queryClient = useQueryClient();
  const { writeContractAsync } = useWriteContract();
  const publicClient = usePublicClient();
  const {
    walletConnected,
    walletAddress,
    network,
    chainId,
    refreshDashboard,
  } = useAppState();

  const [keyword, setKeyword] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [purchaseDraft, setPurchaseDraft] = useState(null);
  const [, setFlowStep] = useState(1);
  const [flowStatus, setFlowStatus] = useState("idle");
  const [message, setMessage] = useState(t(locale, "marketMsgInit"));
  const [purchasing, setPurchasing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const listedAssetsQuery = useQuery({
    queryKey: ["market", "listed-nfts", chainId || 56, network || "unknown", locale],
    queryFn: async () => {
      const result = await apiGetMarketNfts(500, locale);
      const rows = Array.isArray(result?.nfts) ? result.nfts : [];
      return rows
        .filter(
          (item) =>
            String(item?.listingStatus || "").toLowerCase() === "listed",
        )
        .map((item) => {
          const rawPrice = item?.listingPrice ?? item?.currentPrice ?? "";
          const priceText = normalizePriceString(rawPrice);
          const tokenId = resolveTokenId(item);
          const resolvedChainId = Number(item?.chainId || chainId || 56);
          return {
            ...item,
            name: item?.name || `Token #${tokenId || "-"}`,
            artist:
              item?.artist ||
              item?.listedByAddress ||
              t(locale, "marketUnknownSeller"),
            tokenId,
            image: resolveNftImage(item),
            galleryImages: resolveNftGalleryImages(item),
            listedByAddress: item?.listedByAddress,
            type: item?.type || t(locale, "marketListedNftType"),
            currentPrice: priceText || null,
            quoteSymbol: MARKET_QUOTE_SYMBOL,
            chainId: resolvedChainId,
            chainName: network || item?.chainName || `Chain ${resolvedChainId}`,
          };
        });
    },
    staleTime: 20_000,
    retry: 1,
  });

  const purchaseHistoryQuery = useQuery({
    queryKey: ["market", "purchase-history", walletAddress || ""],
    queryFn: async () => {
      const result = await apiGetPurchaseHistory(walletAddress, { limit: 100 });
      return Array.isArray(result?.purchases) ? result.purchases : [];
    },
    enabled: false,
    staleTime: 30_000,
    retry: 1,
  });

  const listedAssets = listedAssetsQuery.data || [];
  const listedLoading =
    listedAssetsQuery.isPending || listedAssetsQuery.isFetching;
  const listedError = listedAssetsQuery.error
    ? String(listedAssetsQuery.error?.message || listedAssetsQuery.error)
    : "";

  const purchaseHistory = purchaseHistoryQuery.data || [];
  const historyLoading =
    purchaseHistoryQuery.isPending || purchaseHistoryQuery.isFetching;
  const historyError = purchaseHistoryQuery.error
    ? String(purchaseHistoryQuery.error?.message || purchaseHistoryQuery.error)
    : "";
  const usdtBalanceQuery = useQuery({
    queryKey: ["market", "usdt-balance", walletAddress || ""],
    queryFn: async () => {
      const raw = await publicClient.readContract({
        address: USDT_CONTRACT_ADDRESS,
        abi: ERC20_ABI,
        functionName: "balanceOf",
        args: [walletAddress],
      });
      return formatEther(raw || 0n);
    },
    enabled: Boolean(publicClient && walletConnected && walletAddress),
    staleTime: 15_000,
    retry: 1,
  });

  const loadListedAssets = useCallback(async () => {
    const result = await listedAssetsQuery.refetch();
    if (result.error) {
      return [];
    }
    return result.data || [];
  }, [listedAssetsQuery]);

  const filtered = useMemo(() => {
    return listedAssets.filter((item) => {
      const matchKeyword =
        !keyword ||
        `${item.name} ${item.artist} ${item.nftAddress}`
          .toLowerCase()
          .includes(keyword.toLowerCase());
      const matchType =
        typeFilter === "all" ||
        String(item.type || "").toLowerCase() === typeFilter.toLowerCase();
      return matchKeyword && matchType;
    });
  }, [listedAssets, keyword, typeFilter]);

  const getQuoteSymbol = useCallback(() => MARKET_QUOTE_SYMBOL, []);

  const getPurchaseButtonLabel = useCallback(
    () => `${t(locale, "marketPurchaseBtn")} (${MARKET_QUOTE_SYMBOL})`,
    [locale],
  );

  const isOwnListingFor = useCallback(
    (asset) =>
      Boolean(
        walletConnected &&
          walletAddress &&
          asset?.listedByAddress &&
          walletAddress.toLowerCase() ===
            String(asset.listedByAddress).toLowerCase(),
      ),
    [walletConnected, walletAddress],
  );

  const buildPurchaseSummary = useCallback(
    (asset) => {
      if (!asset?.currentPrice) return null;
      try {
        const priceWei = parseEther(String(asset.currentPrice));
        return {
          quoteSymbol: MARKET_QUOTE_SYMBOL,
          priceWei,
          totalWei: priceWei,
          total: trimDisplayAmount(formatEther(priceWei)),
        };
      } catch {
        return null;
      }
    },
    [],
  );

  const purchaseSummary = useMemo(
    () => buildPurchaseSummary(purchaseDraft),
    [buildPurchaseSummary, purchaseDraft],
  );
  const purchaseQuoteSymbol = MARKET_QUOTE_SYMBOL;
  const purchaseIsOwnListing = isOwnListingFor(purchaseDraft);
  const localeCode = LOCALES[locale]?.code || "zh-CN";

  const closePurchaseModal = useCallback(() => {
    if (purchasing) return;
    setPurchaseDraft(null);
  }, [purchasing]);

  const openPurchaseModal = useCallback((asset) => {
    setPurchaseDraft(asset || null);
  }, []);

  // Legacy BNB/native path retained for quick rollback only.
  // const executePurchaseByEth = useCallback(
  //   async ({ nftAddress, tokenId, priceWei }) => {
  //     return writeContractAsync({
  //       address: NFT_SALE_CONTRACT_ADDRESS,
  //       abi: nftSaleAbi,
  //       functionName: "purchaseTokenByEth",
  //       args: [nftAddress, tokenId],
  //       value: priceWei,
  //     });
  //   },
  //   [writeContractAsync],
  // );

  const executePurchase = async (assetInput = purchaseDraft) => {
    const asset = assetInput || null;
    if (!asset) {
      setFlowStep(3);
      setFlowStatus("error");
      setMessage(t(locale, "marketMsgNoAsset"));
      return;
    }

    setPurchasing(true);
    setFlowStatus("loading");
    setMessage(t(locale, "marketMsgProcessingPurchase"));

    if (!walletConnected || !walletAddress) {
      setFlowStep(1);
      setFlowStatus("error");
      setMessage(t(locale, "marketMsgConnectWalletLogin"));
      setPurchasing(false);
      return;
    }

    if (asset.chainId && chainId && Number(asset.chainId) !== Number(chainId)) {
      setFlowStep(2);
      setFlowStatus("error");
      setMessage(
        `${t(locale, "marketMsgNetworkMismatch")} ${asset.chainName || asset.chainId}${t(locale, "marketMsgCurrentWalletNetwork")} ${network}.`,
      );
      setPurchasing(false);
      return;
    }

    setFlowStep(4);

    const summary = buildPurchaseSummary(asset);
    if (!summary) {
      setFlowStatus("error");
      setMessage(t(locale, "marketMsgMissingPrice"));
      setPurchasing(false);
      return;
    }
    const {
      quoteSymbol,
      totalWei,
      total,
    } = summary;

    if (!publicClient) {
      setFlowStatus("error");
      setMessage(t(locale, "profileMsgRpcUnavailableShort"));
      setPurchasing(false);
      return;
    }

    if (isOwnListingFor(asset)) {
      setFlowStatus("error");
      setMessage(t(locale, "marketMsgCannotBuyOwn"));
      setPurchasing(false);
      return;
    }

    setFlowStep(5);

    setFlowStep(6);

    const nftAddress = asset?.nftAddress || DEFAULT_NFT_ADDRESS;
    const tokenIdRaw =
      asset?.tokenId ??
      (typeof asset?.id === "string" && asset.id.includes(":")
        ? asset.id.split(":").pop()
        : "");

    let tokenId;
    try {
      tokenId = BigInt(String(tokenIdRaw));
    } catch {
      setFlowStatus("error");
      setMessage(t(locale, "marketMsgInvalidTokenId"));
      setPurchasing(false);
      return;
    }

    let txHash = "";

    try {
      const usdtBalance = await publicClient.readContract({
        address: USDT_CONTRACT_ADDRESS,
        abi: ERC20_ABI,
        functionName: "balanceOf",
        args: [walletAddress],
      });
      if (usdtBalance < totalWei) {
        setFlowStatus("error");
        setMessage(
          `${t(locale, "marketMsgInsufficientBalance")} ${total} ${quoteSymbol}.`,
        );
        setPurchasing(false);
        return;
      }

      const allowance = await publicClient.readContract({
        address: USDT_CONTRACT_ADDRESS,
        abi: ERC20_ABI,
        functionName: "allowance",
        args: [walletAddress, NFT_SALE_CONTRACT_ADDRESS],
      });
      if (allowance < totalWei) {
        setMessage(t(locale, "marketMsgApprovingToken", "Approving USDT spend..."));
        const approveHash = await writeContractAsync({
          address: USDT_CONTRACT_ADDRESS,
          abi: ERC20_ABI,
          functionName: "approve",
          args: [NFT_SALE_CONTRACT_ADDRESS, totalWei],
        });
        await publicClient.waitForTransactionReceipt({
          hash: approveHash,
          timeout: 120_000,
        });
      }

      setMessage(t(locale, "marketMsgSubmittingTx"));
      txHash = await writeContractAsync({
        address: NFT_SALE_CONTRACT_ADDRESS,
        abi: nftSaleAbi,
        functionName: "purchaseTokenByErc20",
        args: [nftAddress, tokenId],
      });

      await publicClient.waitForTransactionReceipt({
        hash: txHash,
        timeout: 120_000,
      });

      setMessage(t(locale, "marketMsgCallingBackend"));
      await apiNotifyShopTx({
        action: "buy",
        txHash,
        source: "market-buy",
        nftAddress,
        tokenId: String(tokenId),
      });

      await refreshDashboard(walletAddress);
      queryClient.removeQueries({ queryKey: ["market", "listed-nfts"] });
      await queryClient.invalidateQueries({ queryKey: ["market", "listed-nfts"] });
      await queryClient.invalidateQueries({
        queryKey: ["market", "purchase-history", walletAddress || ""],
      });
      await loadListedAssets();

      setFlowStep(7);
      setFlowStatus("success");
      setMessage(`${t(locale, "tradeSuccess")} ${t(locale, "marketMsgOrderWritten")} ${txHash}.`);
      setPurchaseDraft(null);
    } catch (error) {
      setFlowStep(6);
      setFlowStatus("error");
      setMessage(`${t(locale, "marketMsgBuyFailed")}${String(error?.message || error)}`);
    } finally {
      setPurchasing(false);
    }
  };

  const typeOptions = useMemo(() => {
    const set = new Set(listedAssets.map((item) => item.type).filter(Boolean));
    return Array.from(set);
  }, [listedAssets]);

  const loadPurchaseHistory = useCallback(async () => {
    if (!walletConnected || !walletAddress) {
      setFlowStatus("error");
      setMessage(t(locale, "marketMsgConnectWalletFirst"));
      return;
    }
    await purchaseHistoryQuery.refetch();
  }, [walletConnected, walletAddress, purchaseHistoryQuery, locale]);

  const handleShowHistory = () => {
    setShowHistory(true);
    loadPurchaseHistory().catch(() => {});
  };

  useEffect(() => {
    setMessage(t(locale, "marketMsgInit"));
  }, [locale]);

  useEffect(() => {
    if (!purchaseDraft?.id || purchasing) return;
    const stillListed = listedAssets.some((item) => item.id === purchaseDraft.id);
    if (!stillListed) {
      setPurchaseDraft(null);
    }
  }, [listedAssets, purchaseDraft?.id, purchasing]);

  return (
    <>
      <Seo
        locale={locale}
        title={`Chronos Arte Node | ${t(locale, "navMarket")}`}
        description={t(locale, "marketDesc")}
        path={buildPath(locale, "market")}
      />

      <Panel title={t(locale, "marketPanelTitle")} description="">
        <div className="tool-row" style={{ marginBottom: "16px" }}>
          <button className="btn btn-primary" onClick={handleShowHistory}>
            {t(locale, "marketHistoryBtn")}
          </button>
        </div>

        <div className="form-grid">
          <label>
            {t(locale, "marketKeyword")}
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder={t(locale, "marketKeywordPlaceholder")}
            />
          </label>
          <label>
            {t(locale, "marketType")}
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="all">{t(locale, "marketTypeAll")}</option>
              {typeOptions.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </label>
          <label>
            {t(locale, "marketCurrentNetwork")}
            <input value={network} readOnly />
          </label>
          <label>
            {t(locale, "marketWalletBalance")}
            <input
              value={`${trimDisplayAmount(usdtBalanceQuery.data || "0", 4)} ${MARKET_QUOTE_SYMBOL}`}
              readOnly
            />
          </label>
        </div>

        {listedError ? (
          <p style={{ marginTop: 12 }}>
            {t(locale, "marketLoadAssetsFailed")}
            {listedError}
          </p>
        ) : null}

        <article className="box market-candidates-box">
          <h3>{t(locale, "marketCandidates")}</h3>
          {listedLoading ? (
            <p className="empty">{t(locale, "marketLoadingListed")}</p>
          ) : filtered.length ? (
            <div className="nft-card-grid market-card-grid">
              {filtered.map((item) => {
                const itemQuoteSymbol = getQuoteSymbol(item);
                const itemSummary = buildPurchaseSummary(item);
                const canOpenPurchase =
                  !listedLoading &&
                  !purchasing;
                const actionLabel = getPurchaseButtonLabel();

                return (
                  <NftCard
                    key={item.id}
                    locale={locale}
                    className="market-candidate-card"
                    image={item.image}
                    galleryImages={item.galleryImages}
                    title={item.name}
                    tokenId={item.tokenId}
                    nftAddress={item.nftAddress || item.nft_address || ""}
                    resolveTokenName
                    price={
                      item.currentPrice != null
                        ? `${trimDisplayAmount(item.currentPrice)} ${itemQuoteSymbol}`
                        : t(locale, "marketNoQuote")
                    }
                    priceLabel={t(locale, "marketListingPrice")}
                    badge={item.type || ""}
                    subtitle={`${t(locale, "marketUnknownSeller")}: ${shortAddress(item.listedByAddress || item.artist)}`}
                  >
                    <div className="nft-card-row">
                      <span className="nft-card-label">{t(locale, "marketAssetChain")}</span>
                      <span className="nft-card-value">{item.chainName || item.chainId || "-"}</span>
                    </div>
                    <div className="nft-card-row">
                      <span className="nft-card-label">{t(locale, "marketTotalCost")}</span>
                      <span className="nft-card-value">
                        {itemSummary?.total ? `${itemSummary.total} ${itemQuoteSymbol}` : "-"}
                      </span>
                    </div>
                    <div className="tool-row nft-card-action-row market-card-action-row">
                      <button
                        className="btn btn-primary"
                        onClick={() => openPurchaseModal(item)}
                        disabled={!canOpenPurchase}
                        title={
                          itemSummary == null
                            ? t(locale, "marketMsgMissingPrice")
                            : undefined
                        }
                      >
                        {actionLabel}
                      </button>
                    </div>
                  </NftCard>
                );
              })}
            </div>
          ) : (
            <p className="empty">{t(locale, "marketNoListedItems")}</p>
          )}
        </article>

        <div className={`status-banner ${flowStatus}`}>
          <strong>
            {flowStatus === "loading"
              ? t(locale, "marketStatusLoading")
              : flowStatus === "success"
                ? t(locale, "marketStatusSuccess")
                : flowStatus === "error"
                  ? t(locale, "marketStatusError")
                  : t(locale, "marketStatusReady")}
          </strong>
          <p>{message}</p>
        </div>
      </Panel>

      {purchaseDraft && (
        <div className="modal-overlay" onClick={closePurchaseModal}>
          <div className="modal-content market-buy-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t(locale, "marketOrderConfirm")}</h2>
              <button className="modal-close" onClick={closePurchaseModal}>
                x
              </button>
            </div>
            <div className="modal-body market-order-modal-body">
              <div className="market-order-layout">
                <section className="market-order-main">
                  <div className="market-order-image-shell">
                    {purchaseDraft.image ? (
                      <img
                        src={purchaseDraft.image}
                        alt={purchaseDraft.name || `Token #${purchaseDraft.tokenId || "-"}`}
                        loading="lazy"
                      />
                    ) : (
                      <div className="market-order-image-empty">
                        {t(locale, "detailNoImage", "No image")}
                      </div>
                    )}
                  </div>

                  <div className="market-order-basic">
                    <h3>{purchaseDraft.name || `Token #${purchaseDraft.tokenId || "-"}`}</h3>
                    {purchaseDraft.type ? (
                      <span className="market-order-badge">{purchaseDraft.type}</span>
                    ) : null}
                  </div>

                  <div className="market-order-info-list">
                    <div className="market-order-info-row">
                      <span>{t(locale, "detailTokenId", "Token ID")}</span>
                      <strong>#{purchaseDraft.tokenId || "-"}</strong>
                    </div>
                    <div className="market-order-info-row">
                      <span>{t(locale, "marketListingPrice")}</span>
                      <strong>
                        {purchaseDraft.currentPrice != null
                          ? `${trimDisplayAmount(purchaseDraft.currentPrice)} ${purchaseQuoteSymbol}`
                          : t(locale, "marketNoQuote")}
                      </strong>
                    </div>
                    <div className="market-order-info-row">
                      <span>{t(locale, "marketTotalCost")}</span>
                      <strong>
                        {purchaseSummary?.total
                          ? `${purchaseSummary.total} ${purchaseQuoteSymbol}`
                          : "-"}
                      </strong>
                    </div>
                    <div className="market-order-info-row">
                      <span>{t(locale, "marketUnknownSeller")}</span>
                      <strong>{shortAddress(purchaseDraft.listedByAddress || purchaseDraft.artist)}</strong>
                    </div>
                    <div className="market-order-info-row">
                      <span>{t(locale, "marketAssetChain")}</span>
                      <strong>{purchaseDraft.chainName || purchaseDraft.chainId || "-"}</strong>
                    </div>
                  </div>
                </section>

                <section className="market-order-side">
                  <h4>{t(locale, "detailDescription", "Description")}</h4>
                  <p>
                    {String(
                      purchaseDraft.description ||
                        purchaseDraft.metadataDescription ||
                        "",
                    ).trim() || t(locale, "detailNoDescription")}
                  </p>

                  {purchaseIsOwnListing ? (
                    <p className="market-buy-alert warn">{t(locale, "marketOwnListingWarning")}</p>
                  ) : null}
                </section>
              </div>

              <div className="tool-row market-buy-actions">
                <button className="btn btn-ghost" onClick={closePurchaseModal} disabled={purchasing}>
                  {t(locale, "auctionHistoryClose", "Close")}
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => executePurchase(purchaseDraft)}
                  disabled={
                    purchasing ||
                    purchaseIsOwnListing ||
                    purchaseSummary == null
                  }
                >
                  {purchasing
                    ? t(locale, "marketPurchasing")
                    : getPurchaseButtonLabel()}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showHistory && (
        <div className="modal-overlay" onClick={() => setShowHistory(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t(locale, "marketHistoryTitle")}</h2>
              <button className="modal-close" onClick={() => setShowHistory(false)}>
                x
              </button>
            </div>
            <div className="modal-body">
              {historyLoading ? (
                <p>{t(locale, "marketLoading")}</p>
              ) : historyError ? (
                <p style={{ color: "#ef4444" }}>
                  {t(locale, "marketLoadFailed")}
                  {historyError}
                </p>
              ) : purchaseHistory.length === 0 ? (
                <p>{t(locale, "marketNoPurchaseHistory")}</p>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>{t(locale, "marketColTime")}</th>
                        <th>{t(locale, "marketColNftName")}</th>
                        <th>{t(locale, "marketColPrice")}</th>
                        <th>{t(locale, "marketColTxHash")}</th>
                        <th>{t(locale, "marketColStatus")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {purchaseHistory.map((item, idx) => (
                        <tr key={item.id || idx}>
                          <td>
                            {item.createdAt
                              ? new Date(item.createdAt).toLocaleString(localeCode)
                              : "-"}
                          </td>
                          <td>{item.assetName || item.nftId || "-"}</td>
                          <td>
                            {item.amount != null
                              ? `${item.amount} ${MARKET_QUOTE_SYMBOL}`
                              : "-"}
                          </td>
                          <td>
                            {item.tx ? (
                              <a
                                href={`https://bscscan.com/tx/${item.tx}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: "#3b82f6",
                                  textDecoration: "underline",
                                }}
                              >
                                {item.tx.slice(0, 10)}...{item.tx.slice(-8)}
                              </a>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td>{item.status || t(locale, "marketCompleted")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
